import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import CompanyModal from '../components/CompanyModal';
import { customerService, Company, CustomerUser } from '../services/customerService';
import { organizationService, OrgMember } from '../services/organizationService';
import { useActiveProject } from '../hooks/useActiveProject';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import BoardControlsButton, { BoardColumn, BoardPrefs, loadBoardPrefs, isColumnVisible } from '../components/BoardControlsButton';
import '../styles/CustomersList.css';
import '../styles/Pages.css';

const ICON_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981'];
const ICON_BGS    = ['#ede9fe', '#fce7f3', '#fef3c7', '#d1fae5'];

const COMPANY_COLUMNS: BoardColumn[] = [
  { key: 'description', label: 'Description' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'owner', label: 'Owner' },
  { key: 'headquarters', label: 'Headquarters' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'insights', label: 'Insights' },
];

const sentinel = '__editablejs__:';
function getPlainText(details: string | null | undefined): string {
  if (!details) return '';
  let str = details;
  if (str.startsWith(sentinel)) str = str.slice(sentinel.length);
  if (str.trimStart().startsWith('[') || str.trimStart().startsWith('{')) {
    try {
      const nodes = JSON.parse(str);
      const extractText = (node: any): string => {
        if (typeof node.text === 'string') return node.text;
        if (Array.isArray(node.children)) return node.children.map(extractText).join('');
        return '';
      };
      return (Array.isArray(nodes) ? nodes : [nodes])
        .map(extractText).join(' ').replace(/\s+/g, ' ').trim().slice(0, 100);
    } catch { /* fall through */ }
  }
  return str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100);
}

const CompanyIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 21V9h6v12" />
    <path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" />
  </svg>
);

const Companies: React.FC = () => {
  const { activeProjectId } = useActiveProject();
  const { currentOrg } = useAuth();
  const { can } = usePermissions();
  const canManage = can('customers');

  const [companies, setCompanies]     = useState<Company[]>([]);
  const [allUsers, setAllUsers]       = useState<CustomerUser[]>([]);
  const [allMembers, setAllMembers]   = useState<OrgMember[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [boardPrefs, setBoardPrefs] = useState<BoardPrefs>(() => loadBoardPrefs('companies:boardControls'));
  const colVisible = (key: string) => isColumnVisible(boardPrefs.columns, key);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      customerService.getCompanies(activeProjectId),
      customerService.getCustomerUsers(activeProjectId),
    ]).then(([cos, users]) => {
      setCompanies(cos);
      setAllUsers(users);
    }).catch(() => setError('Failed to load companies'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [activeProjectId]);

  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(setAllMembers).catch(() => {});
  }, [currentOrg]);

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const handleSaved = (company: Company) => {
    setCompanies(prev => {
      const exists = prev.some(c => c.id === company.id);
      return exists ? prev.map(c => c.id === company.id ? company : c) : [company, ...prev];
    });
    setShowModal(false);
    setEditingCompany(null);
  };

  const handleDelete = async (company: Company, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${company.name}"? This cannot be undone.`)) return;
    try {
      await customerService.deleteCompany(company.id);
      setCompanies(prev => prev.filter(c => c.id !== company.id));
    } catch {
      alert('Failed to delete company.');
    }
  };

  const getMemberName = (userId: string | null) => {
    if (!userId) return null;
    const m = allMembers.find(m => m.user_id === userId);
    return m ? (m.full_name || m.email || null) : null;
  };

  const getOwnerName = (ownerId: string | null) => {
    if (!ownerId) return null;
    const u = allUsers.find(u => u.id === ownerId);
    return u ? u.name : null;
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <div className="page-breadcrumb">
            <div className="page-breadcrumb-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 21V9h6v12" />
                <path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" />
              </svg>
            </div>
            <h1 className="page-breadcrumb-title">Companies</h1>
          </div>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input type="text" placeholder="Search companies..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)} className="search-input" />
            </div>
            <BoardControlsButton columns={COMPANY_COLUMNS} storageKey="companies:boardControls" prefs={boardPrefs} onChange={setBoardPrefs} />
            <button className="customers-add-btn btn-primary-cta" onClick={() => { setEditingCompany(null); setShowModal(true); }}
              disabled={!canManage} title={canManage ? 'Add a new company' : 'Only owners and admins can add companies'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Company
            </button>
          </div>
        </div>

        {loading && <div className="customers-loading">Loading companies…</div>}

        {error && (
          <div className="error-state"><p>{error}</p><button onClick={load}>Retry</button></div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h2>{searchQuery ? 'No companies found' : 'No companies yet'}</h2>
            <p>{searchQuery ? 'Try a different search term' : 'Add your first company to get started'}</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="clist-table-wrap">
            {/* Table header */}
            <div className="clist-header-row">
              <div className="clist-col clist-col-name">Name</div>
              {colVisible('description') && <div className="clist-col clist-col-desc">Description</div>}
              {colVisible('assignee') && <div className="clist-col clist-col-sm">Assignee</div>}
              {colVisible('owner') && <div className="clist-col clist-col-sm">Owner</div>}
              {colVisible('headquarters') && <div className="clist-col clist-col-sm">Headquarters</div>}
              {colVisible('revenue') && <div className="clist-col clist-col-xs">Revenue</div>}
              {colVisible('insights') && <div className="clist-col clist-col-xs">Insights</div>}
              <div className="clist-col clist-col-actions" />
            </div>

            {filtered.map((company, idx) => {
              const colorIdx    = idx % 4;
              const preview     = getPlainText(company.details);
              const assigneeName = getMemberName(company.assignee_id);
              const ownerName   = getOwnerName(company.owner_customer_user_id);
              return (
                <div key={company.id} className="clist-row"
                  onClick={() => { if (canManage) { setEditingCompany(company); setShowModal(true); } }}
                  style={{ cursor: canManage ? 'pointer' : 'default' }}>

                  {/* Name */}
                  <div className="clist-col clist-col-name">
                    <div className="clist-icon" style={{ background: ICON_BGS[colorIdx] }}>
                      <CompanyIcon color={ICON_COLORS[colorIdx]} />
                    </div>
                    <span className="clist-name">{company.name}</span>
                  </div>

                  {/* Description */}
                  {colVisible('description') && (
                    <div className="clist-col clist-col-desc">
                      <span className="clist-meta">{preview || <span className="clist-empty">—</span>}</span>
                    </div>
                  )}

                  {/* Assignee */}
                  {colVisible('assignee') && (
                    <div className="clist-col clist-col-sm">
                      {assigneeName
                        ? <span className="clist-pill clist-pill-purple">{assigneeName}</span>
                        : <span className="clist-empty">—</span>}
                    </div>
                  )}

                  {/* Owner */}
                  {colVisible('owner') && (
                    <div className="clist-col clist-col-sm">
                      {ownerName
                        ? <span className="clist-pill clist-pill-blue">{ownerName}</span>
                        : <span className="clist-empty">—</span>}
                    </div>
                  )}

                  {/* Headquarters */}
                  {colVisible('headquarters') && (
                    <div className="clist-col clist-col-sm">
                      <span className="clist-meta">{company.headquarters || <span className="clist-empty">—</span>}</span>
                    </div>
                  )}

                  {/* Revenue */}
                  {colVisible('revenue') && (
                    <div className="clist-col clist-col-xs">
                      <span className="clist-meta">
                        {company.revenue != null ? `$${company.revenue.toLocaleString()}` : <span className="clist-empty">—</span>}
                      </span>
                    </div>
                  )}

                  {/* Insights */}
                  {colVisible('insights') && (
                    <div className="clist-col clist-col-xs">
                      <span className="clist-badge">0</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="clist-col clist-col-actions" onClick={e => e.stopPropagation()}>
                    {canManage && (
                      <div className="clist-actions">
                        <button className="clist-action-btn"
                          onClick={e => { e.stopPropagation(); setEditingCompany(company); setShowModal(true); }} title="Edit">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                          </svg>
                        </button>
                        <button className="clist-action-btn clist-action-danger"
                          onClick={e => handleDelete(company, e)} title="Delete">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <CompanyModal company={editingCompany}
          onClose={() => { setShowModal(false); setEditingCompany(null); }}
          onSaved={handleSaved} />
      )}
    </Layout>
  );
};

export default Companies;
